<script setup lang="ts">
import type { FormKitFrameworkContext } from '@formkit/core'
import FileUpload from 'vue-upload-component'
import { VIDEO_CONSTRAINTS } from '~/composables/useVideoValidation'

const formkitProps = defineProps<{
  context: FormKitFrameworkContext
}>()

const props = computed(() => formkitProps.context?.attrs?.props || {})
const videoType = computed(() => props.value.videoType || 'desktop')
const constraints = computed(() => VIDEO_CONSTRAINTS[videoType.value])

const { validateVideo } = useVideoValidation()

const file = ref(null)
const videoPreview = ref<string | null>(null)
const validationErrors = ref<string[]>([])
const isValidating = ref(false)
const hasAudioWarning = ref(false)

const accept = 'video/mp4'
const extensions = 'mp4'
const name = ref(`video-${formkitProps.context.id}`)

const uploadRef = ref(null)
const videoRef = ref<HTMLVideoElement | null>(null)
const isHovered = ref(false)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const progressPercent = computed(() => {
  if (!duration.value) return 0
  return (currentTime.value / duration.value) * 100
})

onMounted(() => {
  const value = formkitProps.context._value
  if (value?.file?.fileUrl) {
    videoPreview.value = value.file.fileUrl
    file.value = {
      id: value.file.id,
      thumb: value.file.fileUrl,
      name: value.file.filename || value.file.fileUrl.split('/').at(-1),
      data: {},
    }
  }
})

const inputFile = () => {
  if (file.value?.file instanceof File) {
    formkitProps.context.node.input({
      file: file.value.file,
    })
  } else if (file.value) {
    formkitProps.context.node.input({
      file: {
        fileUrl: file.value.thumb,
        id: file.value.id,
      },
    })
  } else {
    formkitProps.context.node.input(null)
  }
}

const inputFilter = async (newFile, oldFile, prevent) => {
  if (
    newFile &&
    newFile.error === '' &&
    newFile.file &&
    (!oldFile || newFile.file !== oldFile.file)
  ) {
    isValidating.value = true
    validationErrors.value = []

    const validation = await validateVideo(newFile.file, constraints.value)

    if (!validation.isValid) {
      validationErrors.value = validation.errors
      prevent()
      isValidating.value = false
      if (uploadRef.value) {
        uploadRef.value.clear()
      }
      return false
    }

    const URL = window.URL || window.webkitURL
    if (URL) {
      if (videoPreview.value) {
        URL.revokeObjectURL(videoPreview.value)
      }
      videoPreview.value = URL.createObjectURL(newFile.file)
    }

    isValidating.value = false
  }
}

const removeFile = () => {
  if (videoPreview.value) {
    const URL = window.URL || window.webkitURL
    if (URL && videoPreview.value.startsWith('blob:')) {
      URL.revokeObjectURL(videoPreview.value)
    }
  }

  file.value = null
  videoPreview.value = null
  validationErrors.value = []
  hasAudioWarning.value = false

  if (uploadRef.value) {
    uploadRef.value.clear()
  }

  inputFile()
}

const onFileUpdate = (files) => {
  if (validationErrors.value.length === 0 && files.length > 0) {
    file.value = files.at(0) || null
    inputFile()
  } else if (validationErrors.value.length > 0) {
    file.value = null
    inputFile()
  }
}

const updateVideoTime = () => {
  if (videoRef.value) {
    currentTime.value = videoRef.value.currentTime
  }
}

const onVideoLoadedMetadata = () => {
  if (videoRef.value) {
    duration.value = videoRef.value.duration
  }
}

const togglePlayPause = () => {
  if (videoRef.value) {
    if (isPlaying.value) {
      videoRef.value.pause()
      isPlaying.value = false
    } else {
      videoRef.value.play()
      isPlaying.value = true
    }
  }
}

const onVideoPlay = () => {
  isPlaying.value = true
}

const onVideoPause = () => {
  isPlaying.value = false
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

onUnmounted(() => {
  if (videoPreview.value && videoPreview.value.startsWith('blob:')) {
    const URL = window.URL || window.webkitURL
    if (URL) {
      URL.revokeObjectURL(videoPreview.value)
    }
  }
})
</script>

<template>
  <div class="video-upload-wrapper flex flex-col">
    <div
      class="upload bg-gray-50 p-4 border rounded-lg"
      :class="{
        'border-red-500':
          context.node.props.validation?.includes('required') &&
          !context.state.valid,
      }"
    >
      <div
        v-if="!file || validationErrors.length > 0"
        class="text-center py-8 text-gray-700"
      >
        <span>Drag & Drop your video or </span>
        <label
          :for="name"
          class="underline cursor-pointer hover:text-primary-600"
        >
          Browse
        </label>
        <div class="text-xs text-gray-500 mt-2">
          Max: {{ constraints.maxDuration }}s,
          {{ (constraints.maxFileSize / (1024 * 1024)).toFixed(1) }}MB,
          {{ constraints.maxWidth }}×{{ constraints.maxHeight }}px
          <br />
          Format: {{ constraints.requiredFormat }} ({{
            constraints.requiredVideoCodec
          }}
          + {{ constraints.requiredAudioCodec }})
        </div>
      </div>

      <div v-if="isValidating" class="text-center py-4 text-blue-600">
        <div
          class="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full"
        />
        <div class="mt-2">Validating video...</div>
      </div>

      <div
        v-if="validationErrors.length > 0"
        class="bg-red-50 border border-red-200 rounded p-3 mb-3"
      >
        <div class="text-red-800 font-semibold mb-1">Validation Failed:</div>
        <ul class="text-red-700 text-sm list-disc list-inside">
          <li v-for="(error, index) in validationErrors" :key="index">
            {{ error }}
          </li>
        </ul>
      </div>

      <div v-if="videoPreview && !isValidating" class="relative">
        <div
          class="relative flex justify-center items-center overflow-hidden bg-black rounded-lg ring-1 ring-black ring-opacity-5 shadow-sm group"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        >
          <video
            ref="videoRef"
            :src="videoPreview"
            class="max-h-[24rem] w-full object-contain"
            muted
            loop
            playsinline
            @timeupdate="updateVideoTime"
            @loadedmetadata="onVideoLoadedMetadata"
            @play="onVideoPlay"
            @pause="onVideoPause"
          />

          <div
            class="flex flex-col p-2 absolute w-full h-20 top-0 left-0 bg-gradient-to-b from-black/80 via-black/50 space-x-1"
          >
            <div class="flex filename">
              <button
                class="flex justify-center items-center w-6 h-6 bg-black/50 rounded-full ring-white hover:ring-2 flex-shrink-0 mr-1"
                @click.stop.prevent="removeFile"
              >
                <svg
                  class="h-6 w-6 text-white"
                  viewBox="0 0 26 26"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.586 13l-2.293 2.293a1 1 0 0 0 1.414 1.414L13 14.414l2.293 2.293a1 1 0 0 0 1.414-1.414L14.414 13l2.293-2.293a1 1 0 0 0-1.414-1.414L13 11.586l-2.293-2.293a1 1 0 0 0-1.414 1.414L11.586 13z"
                    fill="currentColor"
                    fill-rule="nonzero"
                  />
                </svg>
              </button>
              <div
                class="flex items-center text-gray-300 text-xs h-6 overflow-hidden group-hover:text-white"
              >
                <span class="truncate">{{ file.name }}</span>
              </div>
            </div>
          </div>

          <button
            v-show="isHovered"
            class="absolute inset-0 m-auto flex justify-center items-center w-16 h-16 bg-black/60 rounded-full ring-white hover:ring-2 hover:bg-black/80 transition-all z-10"
            @click.stop.prevent="togglePlayPause"
          >
            <svg
              v-if="!isPlaying"
              class="h-8 w-8 text-white ml-1"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
            <svg
              v-else
              class="h-8 w-8 text-white"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          </button>

          <div
            v-show="isHovered"
            class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-3 transition-opacity z-10"
          >
            <div
              class="relative w-full h-1 bg-gray-600 rounded-full overflow-hidden mb-2"
            >
              <div
                class="absolute top-0 left-0 h-full bg-primary-500 transition-all"
                :style="{ width: `${progressPercent}%` }"
              />
            </div>

            <div class="flex justify-between items-center text-white text-xs">
              <span>{{ formatTime(currentTime) }}</span>
              <span>{{ formatTime(duration) }}</span>
            </div>
          </div>
        </div>
      </div>

      <file-upload
        ref="uploadRef"
        :model-value="file ? [file] : []"
        class="!hidden"
        :extensions="extensions"
        :accept="accept"
        :multiple="false"
        :input-id="name"
        @input-filter="inputFilter"
        @update:model-value="onFileUpdate"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
video {
  @apply rounded;
}
</style>
